/**
 * A classroom, heavily out of focus, with the light slowly moving in it.
 *
 * Drawn rather than photographed, for three reasons. A stock classroom photo means
 * pictures of real children on a page that collects teacher emails, which is the wrong
 * first impression for a project STRATEGY.md deliberately keeps away from anything
 * student-adjacent. It means a licence to track. And it means a few hundred KB for
 * something the eye reads as a blur anyway — this is a few KB of markup.
 *
 * The shapes are only legible as a room because of what they suggest together: light
 * falling through a window, a board on the far wall, desks in rows receding. At the
 * blur radius applied in CSS, none of them resolve into objects.
 *
 * **Why three separate layers rather than one SVG.** The blur is expensive to compute.
 * Animating anything *inside* a blurred element forces the browser to re-run that blur
 * every frame, which is how an ambient background turns into a hot laptop. Each layer
 * here is blurred once, rasterised once, and then only its `transform` animates — a
 * GPU-composited move of a cached texture. It also buys the effect its depth: three
 * layers drifting at different speeds is parallax, where one layer moving is a slide.
 *
 * Decorative, so `aria-hidden`. A screen reader announcing "classroom illustration"
 * would add nothing a teacher needs.
 */
export default function Backdrop() {
  return (
    <div className="backdrop" aria-hidden="true">
      {/* Light through a window, off to the right. The warm bias is the whole mood —
          a cool wash reads as an office, not a classroom. This layer drifts furthest
          and also breathes slightly, because moving light is the thing the eye
          actually notices in a room. */}
      <div className="bd-layer bd-layer-light">
        <svg viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice" focusable="false">
          <ellipse cx="1210" cy="180" rx="520" ry="420" className="bd-sun" />
          <ellipse cx="1020" cy="420" rx="640" ry="480" className="bd-sun-2" />
          <g className="bd-window" transform="rotate(-6 1180 300)">
            <rect x="1010" y="80" width="180" height="290" rx="10" />
            <rect x="1220" y="60" width="180" height="290" rx="10" />
          </g>
        </svg>
      </div>

      {/* The far wall. Furthest away, so it moves least — that difference is what
          sells the depth. */}
      <div className="bd-layer bd-layer-room">
        <svg viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice" focusable="false">
          <rect x="150" y="150" width="560" height="270" rx="14" className="bd-board" />
        </svg>
      </div>

      {/* Desks, in rows. Each row is nearer, so wider and further apart — that
          gradient is what makes the eye read depth instead of scattered blobs. */}
      <div className="bd-layer bd-layer-desks">
        <svg viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice" focusable="false">
          <g className="bd-desks">
            <g className="bd-row-far">
              <rect x="250" y="500" width="150" height="42" rx="12" />
              <rect x="450" y="500" width="150" height="42" rx="12" />
              <rect x="650" y="500" width="150" height="42" rx="12" />
              <rect x="850" y="500" width="150" height="42" rx="12" />
            </g>
            <g className="bd-row-mid">
              <rect x="180" y="600" width="200" height="54" rx="14" />
              <rect x="430" y="600" width="200" height="54" rx="14" />
              <rect x="680" y="600" width="200" height="54" rx="14" />
              <rect x="930" y="600" width="200" height="54" rx="14" />
            </g>
            <g className="bd-row-near">
              <rect x="80" y="730" width="270" height="70" rx="18" />
              <rect x="410" y="730" width="270" height="70" rx="18" />
              <rect x="740" y="730" width="270" height="70" rx="18" />
              <rect x="1070" y="730" width="270" height="70" rx="18" />
            </g>
          </g>
        </svg>
      </div>

      {/* Scrim. The art is decoration; the copy has to stay readable over it in both
          themes, and a fixed opacity on the art alone cannot guarantee that. */}
      <div className="backdrop-scrim" />
    </div>
  );
}
